import "./custom.scss";
import { useContext } from "react";
import { Toaster } from "react-hot-toast";
import { RouteObject, Navigate } from "react-router-dom";
import { useRoutes } from "react-router-dom";

import { getManagerUrl, getAnalyticsUrl } from "@/config";

import ErrorPage from "../src/shared/components/error-page/error-page";
import TemplateList from "./analytics/pages/global_templates/template-list";
import AnalyticsRoutes from "./analytics/routes";
import ManagerRoutes from "./manager/routes";
import PlatformLayout from "./platform/layout/platform-layout";
import AlertCaseList from "./platform/pages/alertcase-list/alertcase-list";
import DatabaseConnection from "./platform/pages/database-connection/database-connection";
import DatamodelGraph from "./platform/pages/datamodel-graph/datamodel-graph";
import DatamodelReportConfigurationList from "./platform/pages/datamodel-report-configuration-list/datamodel-report-configuration-list";
import DatamodelReportDetails from "./platform/pages/datamodel-report-details/datamodel-report-details";
import ReportList from "./platform/pages/datamodel-report-list/datamodel-report-list";
import DatamodelReportNew from "./platform/pages/datamodel-report-new/datamodel-report-new";
import EntityList from "./platform/pages/entity-list/entity-list";
import EnumerationList from "./platform/pages/enumeration-list/enumeration-list";
import EventDetails from "./platform/pages/event-details/event-details";
import EventsList from "./platform/pages/event-list/event-list";
import Home from "./platform/pages/home/home";
import List from "./platform/pages/list/list";
import Login from "./platform/pages/login/login";
import ModelManagement from "./platform/pages/model-management/model-management";
import PasswordChange from "./platform/pages/password-change/password-change";
import TenantList from "./platform/pages/tenant-list/tenant-list";
import TenantNew from "./platform/pages/tenant-new/tenant-new";
import TokenList from "./platform/pages/token-list/token-list";
import UserEdit from "./platform/pages/user-edit/user-edit";
import UserList from "./platform/pages/user-list/user-list";
import UserNew from "./platform/pages/user-new/user-new";
import { roleAdmin, roleRead, roleWrite } from "./shared/constants/user-roles";
import AuthContext from "./shared/context/auth-context";
import { getTheLastParam } from "./shared/utils/utils";
import EntityDetails from "./platform/pages/entity-details/entity-details";
import AlertCaseDetails from "./platform/pages/alertcase-details/alertcase-new";
import TranslationListDetails from "./platform/pages/translation-list-details/translation-list-details";

export interface IRoute {
  route: RouteObject;
  allowed: boolean;
}

function App() {
  const authCtx = useContext(AuthContext);
  let routes: RouteObject = {};
  const rolesArray = authCtx.selectedTenant.roles;
  const hasManager = getManagerUrl() !== "";
  const hasAnalytics = getAnalyticsUrl() !== "";

  const hasSystemAdminRole = authCtx.systemAdmin;
  const hasTenantAdminRole = rolesArray.indexOf(roleAdmin) !== -1;
  const hasReadRole = rolesArray.indexOf(roleRead) !== -1;
  const hasWriteRole = rolesArray.indexOf(roleWrite) !== -1;

  const platformRoutes = () => {
    const routes: IRoute[] = [
      {
        route: { path: "tenants/new", element: <TenantNew /> },
        allowed: hasSystemAdminRole,
      },
      {
        route: { path: "users", element: <UserList /> },
        allowed: hasSystemAdminRole || hasTenantAdminRole,
      },
      {
        route: { path: "/users/new", element: <UserNew /> },
        allowed: hasSystemAdminRole || hasTenantAdminRole,
      },
      {
        route: { path: "/users/:id", element: <UserEdit /> },
        allowed: hasSystemAdminRole || hasTenantAdminRole,
      },
      {
        route: { path: "/tenants", element: <TenantList /> },
        allowed: hasSystemAdminRole || hasTenantAdminRole,
      },
      {
        route: { path: "/tenants/:id", element: <TenantNew /> },
        allowed: hasSystemAdminRole || hasTenantAdminRole,
      },
      {
        route: { path: "/templates", element: <TemplateList /> },
        allowed: hasSystemAdminRole,
      },
      {
        route: { path: "/tokens", element: <TokenList /> },
        allowed: hasTenantAdminRole,
      },
      {
        route: {
          path: "/database-connection",
          element: <DatabaseConnection />,
        },
        allowed: hasTenantAdminRole,
      },
      {
        route: { path: "/entities", element: <EntityList /> },
        allowed: hasTenantAdminRole,
      },
      {
        route: { path: "/events", element: <EventsList /> },
        allowed: hasTenantAdminRole,
      },
      {
        route: { path: "/lists", element: <EnumerationList /> },
        allowed: hasTenantAdminRole,
      },  
      {
        route: { path: "/translation-lists/:id", element: <TranslationListDetails /> },
        allowed: hasTenantAdminRole,
      },
      {
        route: { path: "/translation-lists/new", element: <TranslationListDetails /> },
        allowed: hasTenantAdminRole,
      },
      {
        route: { path: "/alertcasetypes/:id", element: <AlertCaseDetails /> },
        allowed: hasTenantAdminRole,
      },
      {
        route: { path: "/alertcasetypes", element: <AlertCaseList /> },
        allowed: hasTenantAdminRole,
      },
      {
        route: { path: "/alertcasetypes/new", element: <AlertCaseDetails /> },
        allowed: hasTenantAdminRole,
      },
      {
        route: { path: "/events/new", element: <EventDetails /> },
        allowed: hasTenantAdminRole,
      },
      {
        route: { path: "/entities/new", element: <EntityDetails /> },
        allowed: hasTenantAdminRole,
      },
      {
        route: { path: "/lists/new", element: <List /> },
        allowed: hasTenantAdminRole,
      },
      {
        route: { path: "/events/:id", element: <EventDetails /> },
        allowed: hasTenantAdminRole,
      },
      {
        route: { path: "/entities/:id", element: <EntityDetails /> },
        allowed: hasTenantAdminRole,
      },
      {
        route: { path: "/lists/:id", element: <List /> },
        allowed: hasTenantAdminRole,
      },
      {
        route: { path: "/data-monitoring", element: <ModelManagement /> },
        allowed: hasTenantAdminRole || hasWriteRole || hasReadRole,
      },
      {
        route: { path: "/datamodel-graph", element: <DatamodelGraph /> },
        allowed: hasTenantAdminRole || hasWriteRole || hasReadRole,
      },
      {
        route: { path: "/datamodel-reports", element: <ReportList /> },
        allowed: hasTenantAdminRole || hasWriteRole || hasReadRole,
      },
      {
        route: {
          path: "/datamodel-reports/:id",
          element: <DatamodelReportDetails />,
        },
        allowed: hasTenantAdminRole || hasWriteRole || hasReadRole,
      },
      {
        route: {
          path: "/datamodel-reports/new",
          element: <DatamodelReportNew />,
        },
        allowed: hasTenantAdminRole || hasWriteRole || hasReadRole,
      },
      {
        route: {
          path: "/datamodel-reports/configurations",
          element: <DatamodelReportConfigurationList />,
        },
        allowed: hasTenantAdminRole || hasWriteRole,
      },
      {
        route: {
          path: "/datamodel-reports/configurations/:id",
          element: <DatamodelReportNew />,
        },
        allowed: hasTenantAdminRole || hasWriteRole,
      },
    ];

    return routes.filter((route) => route.allowed).map((route) => route.route);
  };

  if (authCtx.isLoggedIn) {
    const accessibleRoutes = [
      { path: "*", element: <ErrorPage /> },
      { path: "/", element: <Home /> },
      { path: "/login", element: <Login /> },
      { path: "/change-password/:id", element: <PasswordChange /> },
      ...platformRoutes(),
    ];
    if (hasAnalytics) {
      accessibleRoutes.push(
        ...AnalyticsRoutes(hasTenantAdminRole, hasReadRole, hasWriteRole)
      );
    }
    if (hasManager) {
      accessibleRoutes.push(
        ...ManagerRoutes(
          hasSystemAdminRole,
          hasTenantAdminRole,
          hasReadRole,
          hasWriteRole
        )
      );
    }
    routes = {
      element: <PlatformLayout />,
      children: accessibleRoutes,
    };
  } else {
    routes = {
      element: <PlatformLayout />,
      children: [
        { path: "*", element: <Navigate replace to={"/login"} /> },
        { path: "/login", element: <Login /> },
      ],
    };
  }

  const routing = useRoutes([routes]);
  return (
    <div className={`routes-${getTheLastParam() !== "login" ? "" : "login"}`}>
      {routing}
      <Toaster
        toastOptions={{
          style: {
            padding: 0,
            margin: 0,
            border: "none",
            boxShadow: "none",
            background: "none",
          },
        }}
      />
    </div>
  );
}
export default App;
